// DropdownMenu.tsx

import React from 'react'

import { Button } from './shadcn-ui'
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from './shadcn-ui/dropdown-menu'

export interface CustomizeDropdownMenuItemProps {
  label: React.ReactNode // Accept ReactNode (includes string and JSX)
  onClick?: () => void
  icon?: React.ReactNode
  disabled?: boolean
  subItems?: CustomizeDropdownMenuItemProps[]
  shortcut?: string
  isCheckbox?: boolean // New property for checkboxes
  checked?: boolean // Checkbox state
  onCheckedChange?: (checked: boolean) => void // Checkbox change handler
}
interface DropdownMenuProps {
  className?: string
  buttonIcon?: React.ReactNode
  buttonLabel?: string
  items?: CustomizeDropdownMenuItemProps[]
  preventClose?: boolean // Add a flag to control preventDefault, default to false
  loginLabel?: string
  onReset?: () => void // Add a reset callback
}

export function DropdownMenuComponent({ className, buttonLabel, items, preventClose, onReset, buttonIcon, loginLabel }: DropdownMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {
          buttonIcon
            ? (
                <Button
                  className={className ? className : 'rounded-md border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100'}
                  color="muted"
                >
                  {buttonIcon}

                  {
                    buttonLabel && (
                      <span className="ml-2">{buttonLabel}</span>

                    )
                  }
                </Button>
              )
            : (
                <Button
                  className={className ? className : 'rounded-md border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100'}
                >
                  {buttonLabel}
                </Button>
              )
        }

      </DropdownMenuTrigger>

      <DropdownMenuContent>
        {
          loginLabel && (
            <>
              <DropdownMenuLabel>{loginLabel}</DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )
        }

        <div className="max-h-60 w-full overflow-y-auto">
          {
            items && items.map((item, index) => (
              <DropdownMenuGroup key={index}>
                {/* Render checkbox if the item isCheckbox is true */}
                {
                  item.isCheckbox
                    ? (
                        <DropdownMenuCheckboxItem
                          checked={item.checked}
                          className="flex items-center space-x-2"
                          disabled={item.disabled}
                          onCheckedChange={item.onCheckedChange}
                          onSelect={
                            (event) => {
                              if (preventClose) {
                                event.preventDefault() // Conditionally prevent closing
                              }
                            }
                          }
                        >
                          <span>{item.label}</span>
                          {item.icon && <span className="mr-4 size-4">{item.icon}</span>}
                        </DropdownMenuCheckboxItem>
                      )
                    : (
                        <DropdownMenuItem
                          className="flex items-center space-x-2"
                          disabled={item.disabled}
                          onClick={item.onClick}
                          onSelect={
                            (event) => {
                              if (preventClose) {
                                event.preventDefault() // Conditionally prevent closing
                              }
                            }
                          }

                        >
                          {item.icon && <span className="mr-4 size-4">{item.icon}</span>}
                          <span>{item.label}</span>
                          {item.shortcut && <DropdownMenuShortcut>{item.shortcut}</DropdownMenuShortcut>}
                        </DropdownMenuItem>
                      )
                }

                {/* Handle subItems if they exist */}
                {
                  item.subItems && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="flex items-center space-x-2">
                        {item.icon && <span className="mr-2 size-4">{item.icon}</span>}
                        <span>{item.label}</span>
                      </DropdownMenuSubTrigger>

                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          {
                            item.subItems.map((subItem, subIndex) => (
                              <DropdownMenuItem
                                key={subIndex}
                                className="flex items-center space-x-2"
                                disabled={subItem.disabled}
                                onClick={subItem.onClick}
                              >
                                {subItem.icon && <span className="mr-2 size-4">{subItem.icon}</span>}
                                <span>{subItem.label}</span>
                                {subItem.shortcut && <DropdownMenuShortcut>{subItem.shortcut}</DropdownMenuShortcut>}
                              </DropdownMenuItem>
                            ))
                          }
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  )
                }
              </DropdownMenuGroup>
            ))
          }
        </div>

        {
          items && items.some(item => item.isCheckbox) && (
            <>
              <DropdownMenuSeparator />

              <div className="flex justify-end">
                <Button
                  className="rounded-md bg-transparent font-medium text-muted-foreground hover:bg-transparent"
                  onClick={onReset} // Call reset logic on click
                >
                  Reset
                </Button>
              </div>
            </>
          )
        }
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
